/*
  Perfect Pass: cart drawer + cart page extras (Bar, 09-28-2026).

  <pp-ship-estimate>: the real shipping price for a US ZIP, from Shopify's own cart API
    (POST cart/prepare_shipping_rates.json, then GET cart/async_shipping_rates.json until the rates are ready).
    Shopify needs the state too and checks it against the ZIP, so the likely state is worked out from the ZIP's first
    three digits; if Shopify rejects the pair (a few prefixes cross a state line) or the prefix is unknown, a state
    picker appears.
    Verified against the live store 09-29-2026: after a rejected address, async_shipping_rates keeps answering with the
    previous address's rates, so a rejected request never goes on to read rates. Requests run one at a time.
    The ZIP is remembered in the visitor's browser; rates are re-checked whenever the cart's contents change.

  <pp-cart-upsell>: "Add to this order" — the complementary products set in the Search & Discovery app for the newest
    cart lines, rendered by sections/pp-cart-upsell-rows.liquid through the Product Recommendations API. Nothing shows
    until pairings exist (Bar, 09-29-2026: no fallback to automatic "related" picks).

  Dawn re-renders the drawer after every cart change, so both elements are rebuilt often: answers are cached for the
  life of the page (rates also in sessionStorage) so they reappear at once instead of flickering.
*/
(() => {
  if (customElements.get('pp-ship-estimate')) return;

  const ZIP_KEY = 'pp-ship-zip';
  const RATES_KEY = 'pp-ship-rates';

  // First three ZIP digits → most likely state. Built 09-29-2026 from a 33,103-ZIP dataset (majority state per
  // prefix): right for 33,044; the other 59 sit in prefixes that cross a state line, where Shopify rejects the pair
  // and the picker takes over.
  const ZIP3 = [
    [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'], [50, 59, 'VT'], [60, 69, 'CT'], [70, 89, 'NJ'],
    [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 200, 'DC'], [201, 201, 'VA'], [203, 203, 'DC'],
    [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'],
    [320, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'], [400, 427, 'KY'], [430, 458, 'OH'],
    [460, 479, 'IN'], [480, 499, 'MI'], [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'], [570, 577, 'SD'],
    [580, 588, 'ND'], [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'], [680, 693, 'NE'],
    [700, 714, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'], [820, 831, 'WY'],
    [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'], [890, 898, 'NV'], [900, 961, 'CA'],
    [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'], [995, 999, 'AK'],
  ];
  const STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut',
    DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
    VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  };

  const stateForZip = (zip) => {
    const p = parseInt(zip.slice(0, 3), 10);
    const hit = ZIP3.find(([lo, hi]) => p >= lo && p <= hi);
    return hit ? hit[2] : '';
  };

  const store = {
    get(area, key) {
      try {
        return JSON.parse(window[area].getItem(key));
      } catch {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, JSON.stringify(value));
      } catch {
        /* storage blocked (private mode): nothing is remembered, everything still works */
      }
    },
  };

  const cartRoute = (path) => `${(window.routes && window.routes.cart_url) || '/cart'}/${path}`;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const money = (amount, currency = 'USD') => {
    const n = Number(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    }).format(n);
  };

  // the free-shipping threshold, from Bar's free-shipping bar section (US only; absent elsewhere)
  const freeShippingCents = () => {
    try {
      const cfg = JSON.parse(document.querySelector('.pp-pb-root .pp-pb-config').textContent);
      return cfg.freeShippingAmount > 0 ? cfg.freeShippingAmount : 0;
    } catch {
      return 0;
    }
  };

  const el = (tag, props = {}, ...children) => {
    const node = Object.assign(document.createElement(tag), props);
    node.append(...children.filter((c) => c !== null && c !== undefined && c !== ''));
    return node;
  };

  // ---------------------------------------------------------------- shipping estimate

  class QuoteError extends Error {
    constructor(kind) {
      super(kind);
      this.kind = kind; // 'address' | 'other'
    }
  }

  let queue = Promise.resolve();
  const quotes = new Map(); // `${zip}|${state}|${signature}` → Promise<rate[]>

  function fetchRates(zip, state) {
    const run = async () => {
      const q = new URLSearchParams({
        'shipping_address[zip]': zip,
        'shipping_address[country]': 'US',
        'shipping_address[province]': state,
      });
      const prep = await fetch(`${cartRoute('prepare_shipping_rates.json')}?${q}`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      if (!prep.ok) {
        const body = await prep.json().catch(() => ({}));
        throw new QuoteError(body.zip || body.province ? 'address' : 'other');
      }
      for (let i = 0; i < 15; i++) {
        await sleep(i ? 700 : 350);
        const res = await fetch(`${cartRoute('async_shipping_rates.json')}?${q}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new QuoteError('other');
        const data = await res.json();
        if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      }
      throw new QuoteError('other');
    };
    const job = queue.then(run, run);
    queue = job.catch(() => {});
    return job;
  }

  function cheapest(rates) {
    return [...rates].sort(
      (a, b) => Number(a.price) - Number(b.price) || (a.delivery_days?.[0] ?? 99) - (b.delivery_days?.[0] ?? 99)
    )[0];
  }

  function rateName(rate) {
    const name = rate.presentment_name || rate.name || '';
    return rate.source === 'usps' && !/usps/i.test(name) ? `USPS ${name}` : name;
  }

  class PpShipEstimate extends HTMLElement {
    connectedCallback() {
      if (this.built) return;
      this.built = true;
      this.signature = this.dataset.signature || '';
      this.total = Number(this.dataset.total) || 0;
      this.build();
      const saved = store.get('localStorage', ZIP_KEY);
      if (!saved || !/^\d{5}$/.test(saved.zip) || !STATES[saved.state]) return this.showForm();

      // the drawer sits closed on every page: only ask Shopify once it is actually on screen
      const cached = store.get('sessionStorage', RATES_KEY);
      if (cached && cached.key === `${saved.zip}|${saved.state}|${this.signature}`) return this.quote(saved.zip, saved.state);
      this.say(`Checking rates to ${saved.zip}…`);
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        this.quote(saved.zip, saved.state);
      });
      io.observe(this);
    }

    build() {
      const id = `pp-ship-${Math.random().toString(36).slice(2, 8)}`;
      this.result = el('p', { className: 'pp-ship__result' });
      this.change = el('button', { type: 'button', className: 'pp-ship__change', textContent: 'Change' });
      this.change.addEventListener('click', () => this.showForm(true));
      this.line = el('div', { className: 'pp-ship__line', hidden: true }, this.result, this.change);

      this.zip = el('input', {
        id: `${id}-zip`,
        className: 'pp-ship__zip',
        type: 'text',
        inputMode: 'numeric',
        autocomplete: 'postal-code',
        maxLength: 5,
        pattern: '[0-9]{5}',
        placeholder: 'ZIP code',
        required: true,
      });
      this.state = el('select', { id: `${id}-state`, className: 'pp-ship__state' });
      this.state.append(
        el('option', { value: '', textContent: 'State' }),
        ...Object.entries(STATES).map(([code, name]) => el('option', { value: code, textContent: name }))
      );
      this.stateWrap = el(
        'span',
        { className: 'pp-ship__state-wrap', hidden: true },
        el('label', { className: 'visually-hidden', htmlFor: this.state.id, textContent: 'State' }),
        this.state
      );
      this.submit = el('button', { type: 'submit', className: 'pp-ship__submit', textContent: 'Check' });
      this.form = el(
        'form',
        { className: 'pp-ship__form', hidden: true, noValidate: true },
        el('label', { className: 'pp-ship__label', htmlFor: this.zip.id, textContent: 'Estimate shipping' }),
        el('span', { className: 'pp-ship__fields' }, this.zip, this.stateWrap, this.submit)
      );
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.onSubmit();
      });
      this.zip.addEventListener('input', () => {
        this.zip.value = this.zip.value.replace(/\D/g, '').slice(0, 5);
        this.stateWrap.hidden = true;
        this.state.value = '';
      });

      this.status = el('p', { className: 'pp-ship__status', hidden: true });
      this.live = el('span', { className: 'visually-hidden', role: 'status' });
      this.replaceChildren(this.line, this.form, this.status, this.live);
    }

    showForm(focus = false) {
      const saved = store.get('localStorage', ZIP_KEY);
      if (saved && !this.zip.value) this.zip.value = saved.zip || '';
      this.line.hidden = true;
      this.form.hidden = false;
      if (focus) this.zip.focus();
    }

    say(text, isError = false) {
      this.status.textContent = text;
      this.status.hidden = !text;
      this.status.classList.toggle('is-error', isError);
      this.live.textContent = text;
    }

    onSubmit() {
      const zip = this.zip.value;
      if (!/^\d{5}$/.test(zip)) {
        this.say('Enter a 5-digit ZIP code.', true);
        this.zip.focus();
        return;
      }
      const state = this.stateWrap.hidden ? stateForZip(zip) : this.state.value;
      if (!state) {
        this.askState('');
        return;
      }
      this.quote(zip, state);
    }

    askState(preset) {
      this.state.value = preset || '';
      this.stateWrap.hidden = false;
      this.say('Which state is this ZIP in?');
      this.state.focus();
    }

    async quote(zip, state) {
      const key = `${zip}|${state}|${this.signature}`;
      const cached = store.get('sessionStorage', RATES_KEY);
      let rates = cached && cached.key === key ? cached.rates : null;

      this.submit.disabled = true;
      if (!rates) {
        this.form.hidden = true;
        this.line.hidden = true;
        this.say(`Checking rates to ${zip}…`);
        this.setAttribute('aria-busy', 'true');
        try {
          if (!quotes.has(key)) quotes.set(key, fetchRates(zip, state));
          rates = await quotes.get(key);
        } catch (e) {
          quotes.delete(key);
          this.removeAttribute('aria-busy');
          this.submit.disabled = false;
          this.showForm();
          this.zip.value = zip;
          if (e.kind === 'address' && this.stateWrap.hidden) this.askState('');
          else if (e.kind === 'address') this.say("That ZIP and state don't match. Check both.", true);
          else this.say('Rates are not available right now. Shipping is calculated at checkout.', true);
          return;
        }
        this.removeAttribute('aria-busy');
        store.set('sessionStorage', RATES_KEY, { key, rates });
      }
      this.submit.disabled = false;
      store.set('localStorage', ZIP_KEY, { zip, state });

      if (!rates.length) {
        this.showForm();
        this.say(`We don't have a rate to ${zip} here. Shipping is calculated at checkout.`, true);
        return;
      }
      this.showRate(zip, cheapest(rates));
    }

    showRate(zip, rate) {
      const free = Number(rate.price) === 0;
      const threshold = freeShippingCents();
      const parts = free
        ? [el('b', { textContent: 'Free shipping' }), ` to ${zip}`]
        : [`Shipping to ${zip}: `, el('b', { textContent: `${money(rate.price, rate.currency)} ${rateName(rate)}` })];
      if (!free && threshold && this.total < threshold) parts.push(` · free over ${money(threshold / 100)}`);
      this.result.replaceChildren(...parts);
      this.say('');
      this.live.textContent = this.result.textContent;
      this.form.hidden = true;
      this.line.hidden = false;
    }
  }

  customElements.define('pp-ship-estimate', PpShipEstimate);

  // ---------------------------------------------------------------- "Add to this order"

  const SHOW = 2;
  const recs = new Map(); // url → Promise<html>

  function loadRows(url) {
    if (!recs.has(url)) {
      recs.set(
        url,
        fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      );
    }
    return recs.get(url);
  }

  class PpCartUpsell extends HTMLElement {
    async connectedCallback() {
      if (this.built) return;
      this.built = true;
      const inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
      const ids = (this.dataset.productIds || '').split(',').filter(Boolean);
      const rows = [];
      const seen = new Set();
      for (const id of ids) {
        const url = `${this.dataset.url}&product_id=${encodeURIComponent(id)}`;
        const html = await loadRows(url);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        for (const row of doc.querySelectorAll('.pp-upsell__row')) {
          const pid = row.dataset.productId;
          if (inCart.has(pid) || seen.has(pid)) continue;
          seen.add(pid);
          rows.push(row);
        }
        if (rows.length >= SHOW) break;
      }
      if (!rows.length || !this.isConnected) return;

      const threshold = freeShippingCents();
      const total = Number(this.dataset.total) || 0;
      const list = this.querySelector('.pp-upsell__list');
      for (const row of rows.slice(0, SHOW)) {
        const price = Number(row.dataset.price) || 0;
        if (threshold && total < threshold && total + price >= threshold) {
          row.querySelector('.pp-upsell__price').append(' · gets you free shipping');
        }
        const node = document.importNode(row, true);
        const add = node.querySelector('button.pp-upsell__add');
        if (add) add.addEventListener('click', () => this.add(add, node));
        list.append(node);
      }
      this.hidden = false;
    }

    async add(button, row) {
      button.disabled = true;
      button.classList.add('is-loading');
      row.querySelector('.pp-upsell__error')?.remove();

      const drawer = this.closest('cart-drawer');
      const page = drawer ? null : document.querySelector('cart-items');
      const sections = drawer
        ? drawer.getSectionsToRender().map((s) => s.id)
        : page.getSectionsToRender().map((s) => s.section);

      try {
        const res = await fetch(window.routes.cart_add_url, {
          ...fetchConfig('json'),
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const state = await res.json();
        if (!res.ok || state.status) throw new Error(state.description || state.message || window.cartStrings.error);

        if (drawer) {
          drawer.renderContents(state);
          // the drawer's inside is rebuilt, which drops the keyboard's place and Dawn's focus trap (its own
          // re-trap runs only on the open transition, which an open drawer never plays): trap again on the
          // new contents, starting at the top, the way Dawn does when the drawer opens (review 10-21, T2-09)
          setTimeout(() => {
            const container = document.getElementById('CartDrawer');
            const first = drawer.querySelector('.drawer__inner') || drawer.querySelector('.drawer__close');
            if (container && first && typeof trapFocus === 'function') trapFocus(container, first);
          });
        } else {
          for (const s of page.getSectionsToRender()) {
            const target = document.getElementById(s.id).querySelector(s.selector) || document.getElementById(s.id);
            target.innerHTML = page.getSectionInnerHTML(state.sections[s.section], s.selector);
          }
          // the tapped button is gone with the old list; the keyboard continues from the new line
          const added = Array.from(document.querySelectorAll('#main-cart-items .cart-item')).find(
            (item) => item.querySelector(`input[name="updates[]"][data-quantity-variant-id="${button.dataset.variantId}"]`)
          );
          (added?.querySelector('a.cart-item__name') || document.querySelector('#main-cart-items a.cart-item__name'))?.focus();
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-cart-upsell', cartData: state, variantId: button.dataset.variantId });
      } catch (e) {
        button.disabled = false;
        button.classList.remove('is-loading');
        row.append(el('p', { className: 'pp-upsell__error', role: 'alert', textContent: e.message }));
      }
    }
  }

  customElements.define('pp-cart-upsell', PpCartUpsell);

  // ---------------------------------------------------------------- "Your cart · N" on the cart page
  // Dawn redraws only the list (.js-contents) after a quantity change, and the heading sits above it, so "· 3"
  // stayed after 3 became 2 (launch review 10-21, T2-07). The list carries the count (data-item-count on the
  // table, sections/main-cart-items.liquid); every redraw copies it into the heading. The drawer re-renders its
  // whole inside, heading included, so it needs nothing.
  const cartCount = document.querySelector('cart-items .title--primary .pp-cart-count');
  const cartList = document.querySelector('#main-cart-items .js-contents');
  if (cartCount && cartList) {
    const sync = () => {
      const count = Number(cartList.querySelector('[data-item-count]')?.dataset.itemCount) || 0;
      const text = count > 0 ? ` · ${count}` : '';
      if (cartCount.textContent !== text) cartCount.textContent = text;
    };
    new MutationObserver(sync).observe(cartList, { childList: true });
    sync();
  }
})();
